/**
 * Support requests this window runs with its own handlers (ADR-0088; main/support-window-bridge.ts).
 *
 * The shell answers the commands it owns (a new job, open, rename, delete). A command
 * that belongs to one job's open workspace (a confirmation) is registered here by that
 * workspace while it is mounted, so the shell can open the job and hand the request on.
 * Nothing here draws anything.
 */

export type SupportWindowHandler = (args: Readonly<Record<string, unknown>>) => Promise<unknown>;

const workspaceHandlers = new Map<string, SupportWindowHandler>();
const key = (command: string, jobId: string) => `${command}\u0000${jobId}`;

/** A job's workspace offers a command while it is mounted; the returned function withdraws it. */
export const registerWorkspaceSupportHandler = (
  command: string,
  jobId: string,
  handler: SupportWindowHandler,
): (() => void) => {
  workspaceHandlers.set(key(command, jobId), handler);
  return () => {
    if (workspaceHandlers.get(key(command, jobId)) === handler)
      workspaceHandlers.delete(key(command, jobId));
  };
};

/** The job's workspace's handler, waiting for the workspace to open (it was just asked to). */
export const workspaceSupportHandler = async (
  command: string,
  jobId: string,
  waitMilliseconds: number,
): Promise<SupportWindowHandler> => {
  const until = Date.now() + waitMilliseconds;
  for (;;) {
    const handler = workspaceHandlers.get(key(command, jobId));
    if (handler) return handler;
    if (Date.now() >= until)
      throw new Error("This job's documents did not open in Paige. Open the job, then try again.");
    await new Promise((resolve) => window.setTimeout(resolve, 200));
  }
};

/** A string argument the main process validated; anything else is a fault, not a request. */
export const supportArgument = (args: Readonly<Record<string, unknown>>, name: string): string => {
  const value = args[name];
  if (typeof value !== 'string' || value.length === 0)
    throw new Error(`The support request is missing ${name}.`);
  return value;
};
